import pb from '../lib/pocketbase';
import { debugLogPartner } from './debugConfig';

export interface ClickEvent {
  partner_id: string;
  partner_campaign_id?: string;
  advertisement_id?: string;
  marketing_campaign_id?: string;
  click_type: 'advertisement_view' | 'partner_link' | 'promo_request' | 'external_redirect' | 'public_link_click';
  user_session_id: string;
  user_agent?: string;
  referrer?: string;
  metadata?: Record<string, any>;
}

export interface ClickAnalytics {
  total_clicks: number;
  total_views: number;
  unique_sessions: number;
  click_rate: number;
  conversion_rate: number;
  public_link_clicks: number;
  promo_requests: number;
  total_displays: number;
  daily_clicks: Array<{
    date: string;
    clicks: number;
    views: number;
  }>;
}

class PocketBaseClickTrackingService {
  private sessionId: string;

  constructor() {
    try {
      // Generate or retrieve session ID
      this.sessionId = this.getOrCreateSessionId();
    } catch (error) {
      console.error('Failed to initialize click tracking service:', error);
      this.sessionId = `fallback_${Date.now()}`;
    }
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('user_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('user_session_id', sessionId);
    }
    return sessionId;
  }

  private getUserInfo() {
    return {
      user_agent: navigator.userAgent,
      referrer: document.referrer || 'direct',
    };
  }

  /**
   * Track when a user views an advertisement
   */
  async trackAdvertisementView(
    partnerId: string,
    partnerCampaignId: string,
    advertisementId: string,
    marketingCampaignId?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const userInfo = this.getUserInfo();
      
      await pb.collection('click_tracking').create({
        partner_id: partnerId,
        partner_campaign_id: partnerCampaignId,
        advertisement_id: advertisementId,
        marketing_campaign_id: marketingCampaignId,
        click_type: 'advertisement_view',
        user_session_id: this.sessionId,
        user_agent: userInfo.user_agent,
        referrer: userInfo.referrer,
        metadata: metadata || {}
      });

      await debugLogPartner(partnerId, '📊 Advertisement view tracked:', {
        partnerId,
        advertisementId,
        sessionId: this.sessionId
      });
    } catch (error) {
      console.error('Failed to track advertisement view:', error);
    }
  }

  /**
   * Track when a user clicks on a partner link or CTA
   */
  async trackPartnerLinkClick(
    partnerId: string,
    partnerCampaignId: string,
    advertisementId?: string,
    marketingCampaignId?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const userInfo = this.getUserInfo();
      
      await pb.collection('click_tracking').create({
        partner_id: partnerId,
        partner_campaign_id: partnerCampaignId,
        advertisement_id: advertisementId,
        marketing_campaign_id: marketingCampaignId,
        click_type: 'partner_link',
        user_session_id: this.sessionId,
        user_agent: userInfo.user_agent,
        referrer: userInfo.referrer,
        metadata: metadata || {}
      });

      await debugLogPartner(partnerId, '🔗 Partner link click tracked:', {
        partnerId,
        partnerCampaignId,
        sessionId: this.sessionId
      });
    } catch (error) {
      console.error('Failed to track partner link click:', error);
    }
  }

  /**
   * Track when a user requests a promo code
   */
  async trackPromoRequest(
    partnerId: string,
    partnerCampaignId: string,
    advertisementId: string,
    email: string,
    marketingCampaignId?: string
  ): Promise<void> {
    try {
      const userInfo = this.getUserInfo();
      
      await pb.collection('click_tracking').create({
        partner_id: partnerId,
        partner_campaign_id: partnerCampaignId,
        advertisement_id: advertisementId,
        marketing_campaign_id: marketingCampaignId,
        click_type: 'promo_request',
        user_session_id: this.sessionId,
        user_agent: userInfo.user_agent,
        referrer: userInfo.referrer,
        metadata: { email }
      });

      await debugLogPartner(partnerId, '🎫 Promo request tracked:', {
        partnerId,
        advertisementId,
        email,
        sessionId: this.sessionId
      });
    } catch (error) {
      console.error('Failed to track promo request:', error);
    }
  }

  /**
   * Track external redirects (when users leave to partner sites)
   */
  async trackExternalRedirect(
    partnerId: string,
    partnerCampaignId: string,
    advertisementId: string,
    targetUrl: string,
    marketingCampaignId?: string
  ): Promise<void> {
    try {
      const userInfo = this.getUserInfo();
      
      await pb.collection('click_tracking').create({
        partner_id: partnerId,
        partner_campaign_id: partnerCampaignId,
        advertisement_id: advertisementId,
        marketing_campaign_id: marketingCampaignId,
        click_type: 'external_redirect',
        user_session_id: this.sessionId,
        user_agent: userInfo.user_agent,
        referrer: userInfo.referrer,
        metadata: { target_url: targetUrl }
      });

      await debugLogPartner(partnerId, '↗️ External redirect tracked:', {
        partnerId,
        advertisementId,
        targetUrl,
        sessionId: this.sessionId
      });
    } catch (error) {
      console.error('Failed to track external redirect:', error);
    }
  }

  /**
   * Track when a magic link is opened (Public Link Click)
   */
  async trackPublicLinkClick(
    partnerId: string,
    marketingCampaignId: string,
    excludedPartnerId: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await debugLogPartner(partnerId, '🎯 trackPublicLinkClick CALLED with:', {
      partnerId,
      marketingCampaignId,
      excludedPartnerId,
      metadata
    });
    
    try {
      const userInfo = this.getUserInfo();
      
      const recordData = {
        partner_id: partnerId, // The partner who shared the link
        marketing_campaign_id: marketingCampaignId,
        click_type: 'public_link_click',
        user_session_id: this.sessionId,
        user_agent: userInfo.user_agent,
        referrer: userInfo.referrer,
        metadata: {
          excluded_partner_id: excludedPartnerId,
          magic_link_url: window.location.href,
          ...metadata
        }
      };
      
      await debugLogPartner(partnerId, '📝 Attempting to create click_tracking record:', recordData);
      
      const result = await pb.collection('click_tracking').create(recordData);
      
      await debugLogPartner(partnerId, '✅ Record created successfully:', result.id);

      await debugLogPartner(partnerId, '🔗 Public link click tracked:', {
        partnerId,
        marketingCampaignId,
        excludedPartnerId,
        sessionId: this.sessionId
      });
    } catch (error: any) {
      console.error('❌ FAILED to track public link click:', error);
      console.error('Error details:', {
        message: error?.message,
        data: error?.data,
        status: error?.status
      });
    }
  }

  /**
   * Get click analytics for a partner by date range
   */
  async getPartnerAnalyticsByDateRange(partnerId: string, startDate: Date, endDate: Date): Promise<ClickAnalytics> {
    return this._getPartnerAnalyticsInternal(partnerId, startDate.toISOString(), endDate.toISOString());
  }

  /**
   * Get click analytics for a partner (defaults to current calendar month)
   */
  async getPartnerAnalytics(partnerId: string, daysBack?: number): Promise<ClickAnalytics> {
    let dateFilter: string;
    let endDateFilter: string | undefined;
    
    if (daysBack !== undefined) {
      // Legacy behavior: use days back
      const dateThreshold = new Date();
      dateThreshold.setDate(dateThreshold.getDate() - daysBack);
      dateFilter = dateThreshold.toISOString();
    } else {
      // Default: current calendar month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      dateFilter = startOfMonth.toISOString();
      
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      endDateFilter = endOfMonth.toISOString();
    }
    
    return this._getPartnerAnalyticsInternal(partnerId, dateFilter, endDateFilter);
  }

  /**
   * Internal method for partner analytics
   */
  private async _getPartnerAnalyticsInternal(partnerId: string, dateFilter: string, endDateFilter?: string): Promise<ClickAnalytics> {
    try {

      // Get the partner's earliest campaign creation date
      // This represents when their ads first became available for display in the network
      const partnerCampaigns = await pb.collection('partner_campaigns').getFullList({
        filter: `partner_id = "${partnerId}"`,
        sort: 'created'
      }).catch(() => []);

      // Get public link clicks where THIS partner's ads were displayed
      // Only count clicks that happened AFTER this partner's first campaign was created
      let totalDisplays = 0;
      if (partnerCampaigns.length > 0) {
        // Use the earliest campaign creation date as the baseline
        const firstCampaignDate = new Date(partnerCampaigns[0].created).toISOString();
        // Use the later of: first campaign date or the date filter (daysBack)
        const effectiveStartDate = firstCampaignDate > dateFilter ? firstCampaignDate : dateFilter;
        
        // First, get ALL public link clicks to see what exists
        const allClicks = await pb.collection('click_tracking').getFullList({
          filter: `click_type = "public_link_click"`,
          sort: '-created'
        });
        
        await debugLogPartner(partnerId, '🔍 ALL public link clicks in system:', allClicks.length);
        for (const click of allClicks) {
          await debugLogPartner(partnerId, '  Click:', {
            id: click.id,
            created: click.created,
            partner_id: click.partner_id,
            displayed_partner_ids: click.metadata?.displayed_partner_ids,
            isAfterEffectiveDate: click.created >= effectiveStartDate,
            includesThisPartner: click.metadata?.displayed_partner_ids?.includes(partnerId)
          });
        }
        
        // Get all public link clicks that happened after this partner joined
        const displayRecords = await pb.collection('click_tracking').getFullList({
          filter: `click_type = "public_link_click" && created >= "${effectiveStartDate}"`,
          sort: '-created'
        });
        
        await debugLogPartner(partnerId, '📊 Total Displays Debug:', {
          partnerId,
          firstCampaignDate,
          effectiveStartDate,
          dateFilter,
          endDateFilter,
          totalPublicLinkClicks: displayRecords.length,
          displayRecordsWithMetadata: displayRecords.map(r => ({
            id: r.id,
            partner_id: r.partner_id,
            excluded_partner_id: r.metadata?.excluded_partner_id,
            displayed_partner_ids: r.metadata?.displayed_partner_ids,
            created: r.created
          }))
        });
        
        // Count clicks where this partner's ads were displayed
        // Check both old format (excluded_partner_id) and new format (displayed_partner_ids array)
        totalDisplays = displayRecords.filter(record => {
          // New format: check if partner is in displayed_partner_ids array
          if (record.metadata?.displayed_partner_ids && Array.isArray(record.metadata.displayed_partner_ids)) {
            return record.metadata.displayed_partner_ids.includes(partnerId);
          }
          // Old format fallback: if excluded_partner_id exists, all OTHER partners were displayed
          // So if this partner is NOT the excluded one, their ads were shown
          if (record.metadata?.excluded_partner_id) {
            return record.metadata.excluded_partner_id !== partnerId && record.partner_id !== partnerId;
          }
          return false;
        }).length;
        
        await debugLogPartner(partnerId, '✅ Total Displays for partner:', partnerId, '=', totalDisplays);
      }

      // Get all tracking events for this partner
      const dateRangeFilter = endDateFilter 
        ? `partner_id = "${partnerId}" && created >= "${dateFilter}" && created <= "${endDateFilter}"`
        : `partner_id = "${partnerId}" && created >= "${dateFilter}"`;
      const records = await pb.collection('click_tracking').getFullList({
        filter: dateRangeFilter,
        sort: '-created'
      });

      // Calculate metrics
      const views = records.filter(r => r.click_type === 'advertisement_view').length;
      const clicks = records.filter(r => 
        r.click_type === 'partner_link' || r.click_type === 'promo_request'
      ).length;
      const promoRequests = records.filter(r => r.click_type === 'promo_request').length;
      const publicLinkClicks = records.filter(r => r.click_type === 'public_link_click').length;
      
      // Unique Visitors = unique sessions from public link clicks only
      const publicLinkClickRecords = records.filter(r => r.click_type === 'public_link_click');
      const uniqueSessions = new Set(publicLinkClickRecords.map(r => r.user_session_id)).size;
      const clickRate = views > 0 ? (clicks / views) * 100 : 0;
      const conversionRate = clicks > 0 ? (promoRequests / clicks) * 100 : 0;

      // Calculate daily breakdown
      const dailyMap = new Map<string, { clicks: number; views: number }>();
      
      records.forEach(record => {
        const date = new Date(record.created).toISOString().split('T')[0];
        if (!dailyMap.has(date)) {
          dailyMap.set(date, { clicks: 0, views: 0 });
        }
        const day = dailyMap.get(date)!;
        
        if (record.click_type === 'advertisement_view') {
          day.views++;
        } else if (record.click_type === 'partner_link' || record.click_type === 'promo_request') {
          day.clicks++;
        }
      });

      const daily_clicks = Array.from(dailyMap.entries())
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        total_clicks: clicks,
        total_views: views,
        unique_sessions: uniqueSessions,
        click_rate: Math.round(clickRate * 100) / 100,
        conversion_rate: Math.round(conversionRate * 100) / 100,
        public_link_clicks: publicLinkClicks,
        promo_requests: promoRequests,
        total_displays: totalDisplays,
        daily_clicks
      };
    } catch (error) {
      console.error('Failed to get partner analytics:', error);
      return {
        total_clicks: 0,
        total_views: 0,
        unique_sessions: 0,
        click_rate: 0,
        conversion_rate: 0,
        public_link_clicks: 0,
        promo_requests: 0,
        total_displays: 0,
        daily_clicks: []
      };
    }
  }

  /**
   * Get real-time click count for a partner campaign
   */
  async getPartnerCampaignClicks(partnerCampaignId: string): Promise<number> {
    try {
      const records = await pb.collection('click_tracking').getFullList({
        filter: `partner_campaign_id = "${partnerCampaignId}" && (click_type = "partner_link" || click_type = "promo_request")`
      });
      return records.length;
    } catch (error) {
      console.error('Failed to get campaign clicks:', error);
      return 0;
    }
  }

  /**
   * Get real-time view count for a partner campaign
   */
  async getPartnerCampaignViews(partnerCampaignId: string): Promise<number> {
    try {
      const records = await pb.collection('click_tracking').getFullList({
        filter: `partner_campaign_id = "${partnerCampaignId}" && click_type = "advertisement_view"`
      });
      return records.length;
    } catch (error) {
      console.error('Failed to get campaign views:', error);
      return 0;
    }
  }

  /**
   * Get unique session count for a partner campaign
   */
  async getPartnerCampaignUniqueSessions(partnerCampaignId: string): Promise<number> {
    try {
      const records = await pb.collection('click_tracking').getFullList({
        filter: `partner_campaign_id = "${partnerCampaignId}"`
      });
      
      const uniqueSessions = new Set(records.map(r => r.user_session_id));
      return uniqueSessions.size;
    } catch (error) {
      console.error('Failed to get unique sessions:', error);
      return 0;
    }
  }
}

// Export singleton instance
export const pbClickTracker = new PocketBaseClickTrackingService();
export default PocketBaseClickTrackingService;
